import React, { useEffect, useState } from 'react';
import { companyUserAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import CompanyUserModal from './CompanyUserModal';
import '../../styles/admin/UserManagement.css';

const CompanyUserManagement = () => {
  const [users, setUsers] = useState([]);
  const { getUserId } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await companyUserAPI.getUsers();
      setUsers(data);
    } catch (err) {
      console.error('Failed to fetch company users:', err);
      setError('ユーザー一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const getRoleName = (role) => {
    const roleNames = {
      system_admin: 'システム管理者',
      company_admin: '企業管理者',
      general: '一般ユーザー',
    };
    return roleNames[role] || role;
  };

  const handleCreate = () => {
    setEditingUser(null);
    setModalOpen(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setModalOpen(true);
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`「${userName}」を削除してもよろしいですか？`)) return;
    try {
      await companyUserAPI.deleteUser(userId);
      alert('ユーザーを削除しました');
      fetchUsers();
    } catch (err) {
      console.error('Failed to delete user:', err);
      alert(err.response?.data?.detail || 'ユーザーの削除に失敗しました');
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingUser(null);
  };

  const handleSaveSuccess = () => {
    handleCloseModal();
    fetchUsers();
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedUsers = () => {
    if (!sortField) return users;
    const sorted = [...users].sort((a, b) => {
      const aStr = String(a[sortField] ?? '').toLowerCase();
      const bStr = String(b[sortField] ?? '').toLowerCase();
      if (aStr < bStr) return sortDirection === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  };

  const canOperate = (u) => {
    return !(u.role === 'system_admin' || u.user_id === getUserId());
  };

  return (
    <div className="user-management">
      <div className="section-header">
        <h2>ユーザー管理（自社）</h2>
        <button onClick={handleCreate} className="btn-primary">+ 新規ユーザーを追加</button>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {loading ? (
        <div className="loading">読み込み中...</div>
      ) : (
        <div className="user-table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('display_name')} style={{ cursor: 'pointer' }}>名前 {sortField === 'display_name' && (sortDirection === 'asc' ? '▲' : '▼')}</th>
                <th onClick={() => handleSort('email')} style={{ cursor: 'pointer' }}>メールアドレス {sortField === 'email' && (sortDirection === 'asc' ? '▲' : '▼')}</th>
                <th onClick={() => handleSort('role')} style={{ cursor: 'pointer' }}>権限 {sortField === 'role' && (sortDirection === 'asc' ? '▲' : '▼')}</th>
                <th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer' }}>作成日時 {sortField === 'created_at' && (sortDirection === 'asc' ? '▲' : '▼')}</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="5" className="no-data">ユーザーが登録されていません</td>
                </tr>
              ) : (
                getSortedUsers().map((user) => (
                  <tr key={user.user_id}>
                    <td>{user.display_name}</td>
                    <td>{user.email}</td>
                    <td><span className={`role-badge role-${user.role}`}>{getRoleName(user.role)}</span></td>
                    <td>{new Date(user.created_at).toLocaleString('ja-JP')}</td>
                    <td className="action-buttons">
                      {canOperate(user) && (
                        <>
                          <button onClick={() => handleEdit(user)} className="btn-edit">編集</button>
                          <button onClick={() => handleDelete(user.user_id, user.display_name)} className="btn-delete">削除</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <CompanyUserModal user={editingUser} onClose={handleCloseModal} onSaveSuccess={handleSaveSuccess} />
      )}
    </div>
  );
};

export default CompanyUserManagement;
